import { supabase } from './supabase';
import { gatewayAuthHeaders } from './gateway-auth';
import type { Camera, ClientCamera, ProbeResult, RegisteredCamera } from '@asi/shared';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL as string | undefined;

/**
 * Call the cameras-proxy Edge Function.
 * This is the single entry point for all Hik-Connect operations.
 * Both web and mobile use this same contract — the Edge Function
 * handles auth (via Supabase JWT) and proxies to the gateway.
 */
async function callCamerasProxy<T = unknown>(
  action: string,
  payload: Record<string, unknown> = {},
): Promise<T> {
  // In dev mode with VITE_GATEWAY_URL, call gateway directly
  if (GATEWAY_URL) {
    return callGatewayDirect<T>(action, payload);
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await supabase.functions.invoke('cameras-proxy', {
    body: { action, ...payload },
  });

  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

/**
 * Direct gateway call (dev mode — bypasses Edge Function).
 */
async function callGatewayDirect<T = unknown>(
  action: string,
  payload: Record<string, unknown> = {},
): Promise<T> {
  const pathMap: Record<string, { path: string; method: string }> = {
    // HCT endpoints
    status:         { path: '/api/hik/status',         method: 'GET' },
    devices:        { path: '/api/hik/devices',        method: 'GET' },
    cameras:        { path: '/api/hik/cameras',        method: 'GET' },
    stream:         { path: '/api/hik/stream',         method: 'POST' },
    snapshot:       { path: '/api/hik/snapshot',       method: 'POST' },
    ptz:            { path: '/api/hik/ptz',            method: 'POST' },
    'nvr-channels': { path: '/api/hik/nvr-channels',   method: 'POST' },
    // HPP endpoints
    'hpp-status':          { path: '/api/hpp/status',           method: 'GET' },
    'hpp-devices':         { path: '/api/hpp/devices',          method: 'GET' },
    'hpp-camera-channels': { path: '/api/hpp/camera-channels',  method: 'POST' },
    // DB endpoints
    'db-cameras':          { path: '/api/db/cameras',           method: 'GET' },
    'db-cameras-delete':   { path: '/api/db/cameras',           method: 'DELETE' },
    'db-cameras-cleanup':  { path: '/api/db/cameras/cleanup',   method: 'POST' },
  };

  const route = pathMap[action];
  if (!route) throw new Error(`Unknown action: ${action}`);

  let url = `${GATEWAY_URL}${route.path}`;

  const init: RequestInit = {
    method: route.method,
    headers: { 'Content-Type': 'application/json', ...gatewayAuthHeaders() },
  };

  if (route.method === 'GET' && Object.keys(payload).length > 0) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(payload)) {
      if (v != null) params.set(k, String(v));
    }
    url += `?${params.toString()}`;
  } else if (route.method === 'DELETE' && Object.keys(payload).length > 0) {
    // DELETE with query params
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(payload)) {
      if (v != null) params.set(k, String(v));
    }
    url += `?${params.toString()}`;
  } else if (route.method === 'POST') {
    init.body = JSON.stringify(payload);
  }

  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error ?? `Gateway error ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── HCT Status ───────────────────────────────────────────────

export async function getHikStatus(): Promise<{
  configured: boolean;
  authenticated: boolean;
}> {
  return callCamerasProxy('status');
}

// ─── Cameras ──────────────────────────────────────────────────

export interface HikCamera {
  id: string;
  name: string;
  online: string;
  abilitySet: string;
  area: { id: string; name: string };
  device: {
    devInfo: {
      id: string;
      category: string;
      serialNo: string;
      streamSecretKey: string;
    };
    channelInfo: {
      id: string;
      no: string;
    };
  };
}

export async function listHikCameras(page = 1, pageSize = 50): Promise<{
  cameras: HikCamera[];
  total: number;
}> {
  return callCamerasProxy('cameras', { page, pageSize });
}

// ─── Sync Hikvision → Supabase ────────────────────────────────

interface NvrChannel {
  id: number;
  name: string;
  online: boolean;
  sourceAddress: string;
  protocolType: string;
}

interface HppCameraChannel {
  deviceSerial: string;
  ipcSerial: string;
  channelNo: number;
  deviceName: string;
  channelName: string;
  status: number;
  isShared: string;
  isEncrypt: number;
  videoLevel: number;
  relatedIpc: boolean;
}

export async function syncHikDevices(): Promise<{ synced: number; total: number; sources: { hct: number; hpp: number } }> {
  // In dev mode, fetch from gateway and upsert directly to Supabase
  if (GATEWAY_URL) {
    const rows: Array<Record<string, unknown>> = [];
    let hctCount = 0;
    let hppCount = 0;

    // ═══════════════════════════════════════════════════════════════════════
    // 1. SYNC FROM HCT (Hik-Connect for Teams) - Cloud cameras + NVR channels
    // ═══════════════════════════════════════════════════════════════════════
    
    try {
      const { cameras } = await callGatewayDirect<{
        cameras: HikCamera[];
        total: number;
      }>('cameras', { pageSize: 500 });

      // Group cameras by device serial to detect NVR channels
      // NVRs have multiple cameras with the same serialNo but different channelInfo.no
      const serialCount = new Map<string, number>();
      for (const c of cameras ?? []) {
        const serial = c.device.devInfo.serialNo;
        serialCount.set(serial, (serialCount.get(serial) ?? 0) + 1);
      }

      for (const c of cameras ?? []) {
        const serial = c.device.devInfo.serialNo;
        const channelNo = parseInt(c.device.channelInfo?.no ?? '1', 10);
        const isNvrChannel = (serialCount.get(serial) ?? 0) > 1;

        if (isNvrChannel) {
          // NVR channel — use SERIAL:CHANNEL as unique ID
          rows.push({
            hikvision_device_id: `${serial}:${channelNo}`,
            name: c.name || `${serial} CH${channelNo}`,
            protocol: 'HIKVISION_HCT' as const,
            host: serial,
            port: channelNo,
            status: c.online === '1' ? ('ONLINE' as const) : ('OFFLINE' as const),
            last_seen_at: c.online === '1' ? new Date().toISOString() : null,
            source: 'HCT',
            device_type: 'nvr_channel',
            nvr_serial: serial,
            channel_no: channelNo,
          });
        } else {
          // Standalone camera — use plain serial as ID
          rows.push({
            hikvision_device_id: serial,
            name: c.name || `Cámara ${serial}`,
            protocol: 'HIKVISION_HCT' as const,
            host: 'hikcentralconnect.com',
            port: 443,
            status: c.online === '1' ? ('ONLINE' as const) : ('OFFLINE' as const),
            last_seen_at: c.online === '1' ? new Date().toISOString() : null,
            source: 'HCT',
            device_type: 'standalone',
          });
        }
        hctCount++;
      }

      // Get HCT devices for NVR channel discovery via ISAPI (fallback for NVRs
      // whose channels don't appear in listCameras — e.g. EZVIZ V2.0 NVRs)
      const { devices } = await callGatewayDirect<{
        devices: Array<{ id: string; name: string; serialNo: string; online: string; category: string }>;
        total: number;
      }>('devices', { pageSize: 500 });

      const cloudSerials = new Set((cameras ?? []).map((c) => c.device.devInfo.serialNo));

      for (const dev of devices ?? []) {
        if (cloudSerials.has(dev.serialNo)) continue;

        try {
          const { channels } = await callGatewayDirect<{ channels: NvrChannel[] }>(
            'nvr-channels',
            { deviceId: dev.id },
          );
          if (!channels || channels.length <= 1) continue;

          for (const ch of channels) {
            const nvrChannelId = `${dev.serialNo}:${ch.id}`;

            rows.push({
              hikvision_device_id: nvrChannelId,
              name: ch.name || `${dev.name} CH${ch.id}`,
              protocol: 'HIKVISION_HCT' as const,
              host: dev.serialNo,
              port: ch.id,
              status: ch.online ? ('ONLINE' as const) : ('OFFLINE' as const),
              last_seen_at: ch.online ? new Date().toISOString() : null,
              // New fields
              source: 'HCT',
              device_type: 'nvr_channel',
              nvr_serial: dev.serialNo,
              channel_no: ch.id,
            });
            hctCount++;
          }
        } catch {
          // Channel discovery may fail for non-NVR devices
        }
      }
    } catch (err) {
      console.warn('HCT sync failed, continuing with HPP:', err);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // 2. HPP (Hik-Partner Pro) — alarm panels only, no camera sync needed.
    //    All cameras/NVRs are in HCT now (migrated 2026-04-15).
    //    HPP camera discovery removed — was causing 502 errors for devices
    //    that were deleted from HPP.
    // ═══════════════════════════════════════════════════════════════════════

    // ═══════════════════════════════════════════════════════════════════════
    // 3. UPSERT TO DATABASE
    // ═══════════════════════════════════════════════════════════════════════

    if (rows.length === 0) {
      return { synced: 0, total: 0, sources: { hct: 0, hpp: 0 } };
    }

    const { error } = await supabase
      .from('cameras')
      .upsert(rows, { onConflict: 'hikvision_device_id' });

    if (error) throw new Error(error.message);
    return { synced: rows.length, total: rows.length, sources: { hct: hctCount, hpp: hppCount } };
  }

  // Production: Edge Function handles everything
  const result = await callCamerasProxy<{ synced: number; total: number }>('sync');
  return { ...result, sources: { hct: result.synced, hpp: 0 } };
}

// ─── Live Stream ──────────────────────────────────────────────

export async function getHlsStream(
  deviceSerial: string,
  protocol = 2,
  quality = 1,
): Promise<{ id: string; url: string; expireTime: number }> {
  return callCamerasProxy('stream', { deviceSerial, protocol, quality });
}

// ─── Snapshot ─────────────────────────────────────────────────

export async function getSnapshot(
  deviceSerial: string,
  channelNo = 1,
): Promise<{ url: string }> {
  return callCamerasProxy('snapshot', { deviceSerial, channelNo });
}

// ─── PTZ Control ──────────────────────────────────────────────

export type PtzAction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT' | 'ZOOM_IN' | 'ZOOM_OUT' | 'STOP';

export async function sendPtzCommand(
  deviceSerial: string,
  channelNo: number,
  action: PtzAction,
  speed = 50,
): Promise<void> {
  await callCamerasProxy('ptz', { deviceSerial, channelNo, action, speed });
}

// ─── Supabase DB Operations ───────────────────────────────────

/**
 * Update camera status in Supabase (used after gateway health checks).
 */
export async function updateCameraStatus(
  cameraId: string,
  status: Camera['status'],
): Promise<void> {
  await supabase
    .from('cameras')
    .update({ status, last_seen_at: new Date().toISOString() })
    .eq('id', cameraId);
}

export async function updateCameraName(
  cameraId: string,
  name: string,
): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('El nombre no puede estar vacío');
  const { error } = await supabase
    .from('cameras')
    .update({ name: trimmed })
    .eq('id', cameraId);
  if (error) throw new Error(error.message);
}

/**
 * Flip the public-demo flag on a camera. When true, the camera is visible
 * to every demo user (`profiles.is_demo=true`) without needing a
 * `client_cameras` row. Use sparingly — these cameras leak to anyone who
 * joins the public demo via the mobile onboarding Option C.
 */
export async function setCameraPublicDemo(
  cameraId: string,
  isPublicDemo: boolean,
): Promise<void> {
  const { error } = await supabase
    .from('cameras')
    .update({ is_public_demo: isPublicDemo })
    .eq('id', cameraId);
  if (error) throw new Error(error.message);
}

// ─── Client ↔ Camera Assignment ───────────────────────────────

/** Get all client_cameras assignments for a specific camera. */
export async function getCameraAssignments(cameraId: string): Promise<
  Array<ClientCamera & { client: { id: string; full_name: string; email: string } }>
> {
  const { data, error } = await supabase
    .from('client_cameras')
    .select('*, client:profiles!client_id(id, full_name, email)')
    .eq('camera_id', cameraId);
  if (error) throw new Error(error.message);
  return (data ?? []) as Array<ClientCamera & { client: { id: string; full_name: string; email: string } }>;
}

/** Assign a camera to a client. */
export async function assignCameraToClient(
  cameraId: string,
  clientId: string,
  alias?: string,
): Promise<void> {
  const { error } = await supabase
    .from('client_cameras')
    .upsert(
      { camera_id: cameraId, client_id: clientId, alias: alias ?? null },
      { onConflict: 'client_id,camera_id' },
    );
  if (error) throw new Error(error.message);
}

/** Unassign a camera from a client. */
export async function unassignCameraFromClient(
  cameraId: string,
  clientId: string,
): Promise<void> {
  const { error } = await supabase
    .from('client_cameras')
    .delete()
    .eq('camera_id', cameraId)
    .eq('client_id', clientId);
  if (error) throw new Error(error.message);
}

/** List all clients (for assignment dropdown). */
export async function listClients(): Promise<
  Array<{ id: string; full_name: string; email: string }>
> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email')
    .eq('role', 'client')
    .eq('is_active', true)
    .order('full_name');
  if (error) throw new Error(error.message);
  return (data ?? []) as Array<{ id: string; full_name: string; email: string }>;
}

// ─── Camera Cleanup Operations ────────────────────────────────

/**
 * Delete cameras from database by pattern (e.g., "GB2595385:%" for NVR channels).
 */
export async function deleteCamerasByPattern(
  pattern: string,
): Promise<{ deleted: number; cameras: Array<{ id: string; name: string; deviceId: string }> }> {
  if (!GATEWAY_URL) {
    // Production: delete directly via Supabase
    const { data, error } = await supabase
      .from('cameras')
      .delete()
      .like('hikvision_device_id', pattern)
      .select('id, name, hikvision_device_id');
    if (error) throw new Error(error.message);
    return {
      deleted: data?.length ?? 0,
      cameras: (data ?? []).map(c => ({
        id: c.id,
        name: c.name,
        deviceId: c.hikvision_device_id,
      })),
    };
  }
  // Dev mode: use gateway
  return callGatewayDirect('db-cameras-delete', { pattern });
}

/**
 * Delete a single camera by ID.
 */
export async function deleteCameraById(cameraId: string): Promise<void> {
  const { error } = await supabase
    .from('cameras')
    .delete()
    .eq('id', cameraId);
  if (error) throw new Error(error.message);
}

/**
 * Delete multiple cameras by ID.
 */
export async function deleteCamerasByIds(cameraIds: string[]): Promise<{ deleted: number }> {
  const { data, error } = await supabase
    .from('cameras')
    .delete()
    .in('id', cameraIds)
    .select('id');
  if (error) throw new Error(error.message);
  return { deleted: data?.length ?? 0 };
}

// ─── Camera-Site Linking ──────────────────────────────────────

/**
 * Link a camera to an alarm site for video verification.
 */
export async function linkCameraToSite(
  cameraId: string,
  siteId: string | null
): Promise<void> {
  const { error } = await supabase
    .from('cameras')
    .update({ site_id: siteId })
    .eq('id', cameraId);
  if (error) throw new Error(error.message);
}

/**
 * Get cameras linked to a specific site.
 */
export async function getCamerasBySite(siteId: string): Promise<Camera[]> {
  const { data, error } = await supabase
    .from('cameras')
    .select('*')
    .eq('site_id', siteId)
    .order('name');
  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Get all cameras with their linked site info.
 */
export async function getCamerasWithSites(): Promise<
  Array<Camera & { site_name?: string; site_address?: string }>
> {
  const { data, error } = await supabase
    .from('site_cameras')
    .select('*')
    .order('name');
  if (error) throw new Error(error.message);
  return data ?? [];
}

// ─── Camera Disable / Enable ─────────────────────────────────

export async function disableCamera(
  cameraId: string,
  opts: { reason?: string; siteId?: string | null; accountId?: string | null; deviceName: string }
): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('cameras')
    .update({ is_disabled: true, disabled_at: now, disabled_reason: opts.reason ?? null })
    .eq('id', cameraId);
  if (error) throw new Error(error.message);

  await supabase.from('device_deactivation_log').insert({
    device_type: 'camera',
    device_id: cameraId,
    device_name: opts.deviceName,
    site_id: opts.siteId ?? null,
    account_id: opts.accountId ?? null,
    action: 'disabled',
    reason: opts.reason ?? null,
  });
}

export async function enableCamera(
  cameraId: string,
  opts: { deviceName: string; siteId?: string | null; accountId?: string | null }
): Promise<void> {
  const { error } = await supabase
    .from('cameras')
    .update({ is_disabled: false, disabled_at: null, disabled_reason: null })
    .eq('id', cameraId);
  if (error) throw new Error(error.message);

  await supabase.from('device_deactivation_log').insert({
    device_type: 'camera',
    device_id: cameraId,
    device_name: opts.deviceName,
    site_id: opts.siteId ?? null,
    account_id: opts.accountId ?? null,
    action: 'enabled',
    reason: null,
  });
}

/**
 * Get cameras available for linking (not yet linked or linked to a different site).
 */
export async function getAvailableCamerasForSite(
  _currentSiteId?: string
): Promise<Camera[]> {
  let query = supabase
    .from('cameras')
    .select('*')
    .order('name');

  // Only show cameras not yet assigned to any site
  query = query.is('site_id', null);

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data ?? [];
}

// ─── Device Discovery ─────────────────────────────────────

/**
 * Probe a device via the gateway (ONVIF → ISAPI → RTSP fallback).
 */
export async function probeDevice(params: {
  host: string;
  username: string;
  password: string;
  onvifPort?: number;
  rtspPort?: number;
}): Promise<ProbeResult> {
  if (!GATEWAY_URL) throw new Error('Discovery requires gateway (VITE_GATEWAY_URL)');

  const res = await fetch(`${GATEWAY_URL}/api/discovery/probe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...gatewayAuthHeaders() },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error ?? `Probe failed: ${res.status}`);
  }
  return res.json() as Promise<ProbeResult>;
}

/**
 * Fetch a snapshot JPEG from a discovered channel via the gateway.
 * Returns an object URL that must be revoked after use.
 */
export async function fetchSnapshot(params: {
  host: string;
  username: string;
  password: string;
  channelNo: number;
  rtspPort?: number;
}): Promise<string> {
  if (!GATEWAY_URL) throw new Error('Snapshot requires gateway (VITE_GATEWAY_URL)');

  const res = await fetch(`${GATEWAY_URL}/api/discovery/snapshot`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...gatewayAuthHeaders() },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    throw new Error(`Snapshot failed: ${res.status}`);
  }

  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Conflict reported by `registerDiscoveredChannels` when a channel cannot be
 * registered because the same physical camera (matched by serial-derived
 * stream id) is already active in another row — typically at a different
 * site. The operator must explicitly confirm to unlink the existing
 * registration before retrying with `force: true`.
 */
export type RegisterConflict = {
  channelNo: number;
  streamId: string;
  existingCameraId: string;
  existingName: string;
  existingHost: string | null;
  existingSiteId: string | null;
  existingSiteName: string | null;
};

/**
 * Register discovered channels as cameras in DB + go2rtc.
 *
 * If any channel collides with an active camera that uses the same stream id
 * (same physical device already registered elsewhere), the response includes
 * a `conflicts` array and those channels are skipped. To force re-assignment
 * (the existing rows will be disabled), pass `force: true`.
 */
export async function registerDiscoveredChannels(params: {
  host: string;
  username: string;
  password: string;
  onvifPort?: number;
  rtspPort?: number;
  siteId?: string;
  channels: number[];
  namePrefix?: string;
  names?: Record<number, string>;
  force?: boolean;
}): Promise<{
  device: { manufacturer: string; model: string; serialNumber: string; type: string };
  registered: RegisteredCamera[];
  conflicts: RegisterConflict[];
  total: number;
}> {
  if (!GATEWAY_URL) throw new Error('Registration requires gateway (VITE_GATEWAY_URL)');

  const res = await fetch(`${GATEWAY_URL}/api/discovery/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...gatewayAuthHeaders() },
    body: JSON.stringify(params),
  });

  // 409 = conflicts only, nothing registered. Body still contains the JSON
  // payload with the `conflicts` array — return it so the caller can prompt
  // the operator instead of throwing a generic error.
  if (res.status === 409) {
    const body = await res.json().catch(() => null);
    if (body && Array.isArray(body.conflicts)) {
      return { ...body, registered: body.registered ?? [], conflicts: body.conflicts };
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error ?? `Registration failed: ${res.status}`);
  }
  const body = await res.json();
  return { ...body, conflicts: body.conflicts ?? [] };
}

// Registration code alphabet (no ambiguous chars: 0/O, 1/I/L)
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

function generateCode(length = 8): string {
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join('');
}

/**
 * Generate registration codes for cameras (for QR stickers).
 * Works directly via Supabase — no gateway needed.
 */
export async function generateRegistrationCodes(
  cameraIds: string[],
): Promise<Array<{ cameraId: string; registrationCode: string }>> {
  const results: Array<{ cameraId: string; registrationCode: string }> = [];

  for (const id of cameraIds) {
    const code = generateCode();
    const { error } = await supabase
      .from('cameras')
      .update({ registration_code: code })
      .eq('id', id);
    if (!error) results.push({ cameraId: id, registrationCode: code });
  }

  return results;
}

/**
 * Look up a camera by registration code (QR scan result).
 * Works directly via Supabase — no gateway needed.
 */
export async function lookupByCode(code: string): Promise<{
  camera: {
    id: string;
    name: string;
    status: string;
    deviceType: string;
    manufacturer: string;
    model: string;
    hasStream: boolean;
    siteId: string | null;
  };
}> {
  const sanitized = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (sanitized.length < 6 || sanitized.length > 12) {
    throw new Error('Código inválido');
  }

  const { data, error } = await supabase
    .from('cameras')
    .select('id, name, status, device_type, device_manufacturer, device_model, go2rtc_stream_id, site_id')
    .eq('registration_code', sanitized)
    .single();

  if (error || !data) throw new Error('Cámara no encontrada con ese código');

  return {
    camera: {
      id: data.id,
      name: data.name,
      status: data.status,
      deviceType: data.device_type ?? 'unknown',
      manufacturer: data.device_manufacturer ?? '',
      model: data.device_model ?? '',
      hasStream: !!data.go2rtc_stream_id,
      siteId: data.site_id,
    },
  };
}

// ─── Registered Cameras (for Discovery panel) ─────────────

export interface RegisteredCameraInfo {
  id: string;
  name: string;
  status: string;
  host: string | null;
  channelNo: number | null;
  nvrSerial: string | null;
  streamId: string | null;
  registrationCode: string | null;
  source: string | null;
  createdAt: string;
  siteId?: string | null;
  siteName?: string | null;
  isDisabled?: boolean;
}

/**
 * Fetch all VPN-registered cameras (source = 'VPN').
 * Used by DiscoveryPanel to show already-registered cameras and detect duplicates.
 */
export async function getRegisteredVpnCameras(): Promise<RegisteredCameraInfo[]> {
  const { data, error } = await supabase
    .from('cameras')
    .select('id, name, status, host, channel_no, nvr_serial, go2rtc_stream_id, registration_code, source, created_at')
    .eq('source', 'GATEWAY')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);

  return (data ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    status: c.status,
    host: c.host,
    channelNo: c.channel_no,
    nvrSerial: c.nvr_serial,
    streamId: c.go2rtc_stream_id,
    registrationCode: c.registration_code,
    source: c.source,
    createdAt: c.created_at,
  }));
}

/**
 * Get registered VPN cameras scoped to a specific account (via alarm_sites join).
 */
export async function getRegisteredVpnCamerasForAccount(accountId: string): Promise<RegisteredCameraInfo[]> {
  // Only cameras of THIS account's sites. Cameras without a site belong to no account and used to
  // show up in every account (and "Eliminar todas" would delete them too).
  const { data: siteData, error: siteError } = await supabase
    .from('alarm_sites')
    .select('id, name')
    .eq('account_id', accountId);
  if (siteError) throw new Error(siteError.message);

  const siteNames = new Map((siteData ?? []).map((s) => [s.id as string, s.name as string]));
  if (siteNames.size === 0) return [];

  const { data, error } = await supabase
    .from('cameras')
    .select('id, name, status, host, channel_no, nvr_serial, go2rtc_stream_id, registration_code, source, created_at, site_id, is_disabled')
    .eq('source', 'GATEWAY')
    .in('site_id', [...siteNames.keys()])
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);

  return (data ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    status: c.status,
    host: c.host,
    channelNo: c.channel_no,
    nvrSerial: c.nvr_serial,
    streamId: c.go2rtc_stream_id,
    registrationCode: c.registration_code,
    source: c.source,
    createdAt: c.created_at,
    siteId: c.site_id,
    siteName: siteNames.get(c.site_id) ?? null,
    isDisabled: c.is_disabled ?? false,
  }));
}

// ─── go2rtc Stream Cleanup ────────────────────────────────

/**
 * Remove orphaned go2rtc streams that no longer exist in DB.
 */
export async function cleanupOrphanStreams(): Promise<{
  removed: string[];
  kept: string[];
  totalBefore: number;
  totalAfter: number;
}> {
  if (!GATEWAY_URL) throw new Error('Cleanup requires gateway (VITE_GATEWAY_URL)');

  const res = await fetch(`${GATEWAY_URL}/api/discovery/cleanup-streams`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...gatewayAuthHeaders() },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error ?? `Cleanup failed: ${res.status}`);
  }
  return res.json();
}

// ─── Stream Configuration (ONVIF / ISAPI) ─────────────────

export interface StreamChannelInfo {
  id: number;
  codec: string;
  width: number;
  height: number;
  fps: number;
  bitrate: number;
  streamType: 'main' | 'sub';
  channelNo: number;
}

export interface StreamCapabilities {
  codecs: string[];
  resolutions: Array<{ width: number; height: number }>;
  fpsRange: { min: number; max: number };
  bitrateRange: { min: number; max: number };
}

export interface ConfigureStreamResult {
  protocol: string;
  channelId: number;
  message: string;
  before: StreamChannelInfo | null;
  after: StreamChannelInfo | null;
}

/**
 * Fetch all streaming channels from a device via the device protocol layer.
 */
export async function getStreamChannels(params: {
  host: string;
  username: string;
  password: string;
  port?: number;
}): Promise<{ protocol: string; channels: StreamChannelInfo[] }> {
  if (!GATEWAY_URL) throw new Error('Stream config requires gateway (VITE_GATEWAY_URL)');

  const res = await fetch(`${GATEWAY_URL}/api/discovery/stream-channels`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...gatewayAuthHeaders() },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error ?? `Stream channels failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Get supported configuration options for a specific streaming channel.
 */
export async function getStreamCapabilities(params: {
  host: string;
  username: string;
  password: string;
  port?: number;
  channelId: number;
}): Promise<{ protocol: string; capabilities: StreamCapabilities }> {
  if (!GATEWAY_URL) throw new Error('Stream config requires gateway (VITE_GATEWAY_URL)');

  const res = await fetch(`${GATEWAY_URL}/api/discovery/stream-capabilities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...gatewayAuthHeaders() },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error ?? `Capabilities failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Change a streaming channel's configuration (resolution, codec, fps, bitrate).
 */
export async function configureStream(params: {
  host: string;
  username: string;
  password: string;
  port?: number;
  channelId: number;
  config: {
    width?: number;
    height?: number;
    codec?: string;
    fps?: number;
    bitrate?: number;
  };
}): Promise<ConfigureStreamResult> {
  if (!GATEWAY_URL) throw new Error('Stream config requires gateway (VITE_GATEWAY_URL)');

  const res = await fetch(`${GATEWAY_URL}/api/discovery/configure-stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...gatewayAuthHeaders() },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error ?? `Configure stream failed: ${res.status}`);
  }
  return res.json();
}

// ─── Network Scan / Auto-Discovery ────────────────────────

export interface ScannedChannel {
  channelNo: number;
  name: string;
  codec: string;
  width: number;
  height: number;
  imported: boolean;
  cameraId: string | null;
  cameraName: string | null;
}

export interface ScannedDevice {
  host: string;
  httpPort: number;
  reachable: boolean;
  device: {
    manufacturer: string;
    model: string;
    serialNumber: string;
    firmwareVersion: string;
    deviceType: 'nvr' | 'camera' | 'unknown';
    macAddress: string;
  } | null;
  channelCount: number;
  channels: ScannedChannel[];
  imported: boolean;
  cameras: Array<{ id: string; name: string; channelNo: number | null; streamId: string | null }>;
  authError?: boolean;
}

export interface ScanResult {
  subnet: string;
  scanned: number;
  alive: number;
  devices: ScannedDevice[];
  elapsed: number;
}

/**
 * Scan a VPN subnet for reachable Hikvision NVRs/cameras.
 * Returns discovered devices with their import status.
 */
export async function scanNetwork(params: {
  subnet: string;
  username?: string;
  password: string;
  httpPort?: number;
  startHost?: number;
  endHost?: number;
}): Promise<ScanResult> {
  if (!GATEWAY_URL) throw new Error('Scan requires gateway (VITE_GATEWAY_URL)');

  const res = await fetch(`${GATEWAY_URL}/api/discovery/scan-network`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...gatewayAuthHeaders() },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error ?? `Scan failed: ${res.status}`);
  }
  return res.json();
}
